import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import Input from "../../Shared/Input";
import TextArea from "../../Shared/TextArea";
import AvatarUpload from "../../Shared/AvatarUpload";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import AlertModal from "../../Shared/AlertModal";
import BookingPageFormSkeleton from "../../Shared/LoadingSkeletons/BookingPageFormSkeleton";
import { useBookingPage, useUpdateGeneralTab } from "../../../hooks/useBookingPage";
import { validateImageFile } from "../../../utility/imageUploadValidation";
import {
  buildApiErrorMessage,
  createTrimHandler,
} from "../../../utility/formValidationUtils";

/**
 * PersonalIdentityForm — the "Personal Identity" section of the booking page:
 * provider name, profile photo, and bio. These are the personal, provider-facing
 * fields shown in the public hero.
 *
 * All fields persist to PATCH /booking-page/general via useUpdateGeneralTab.
 */
const PersonalIdentityForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // File state
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);

  // Track original profile photo URL for removal detection.
  // Using a ref instead of state to avoid including it in the hydration
  // effect's dep array — state would create a reset loop where the effect
  // calls setOriginalProfilePhotoUrl, re-runs, and wipes in-progress edits.
  const originalProfilePhotoUrlRef = useRef(null);

  // Removal confirmation dialog
  const [showPhotoRemoveConfirm, setShowPhotoRemoveConfirm] = useState(false);

  // Character count for bio
  const maxCharacters = 500;
  const [charCount, setCharCount] = useState(0);

  const { data: configData, isLoading } = useBookingPage();

  const { mutate: updateGeneral, isPending: isUpdating } =
    useUpdateGeneralTab();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { isDirty, errors: formErrors },
  } = useForm({
    defaultValues: {
      providerName: "",
      profilePhotoUrl: "",
      providerBio: "",
    },
  });

  // Populate form when data loads
  useEffect(() => {
    if (configData?.data) {
      const incoming = configData.data.profilePhotoUrl || null;

      if (incoming !== originalProfilePhotoUrlRef.current) {
        // Server returned a different photo URL — clear any pending file
        // selection so we don't upload a stale file after a save.
        setProfilePhotoFile(null);
        originalProfilePhotoUrlRef.current = incoming;
      }

      reset(
        {
          providerName: configData.data.providerName || "",
          profilePhotoUrl: configData.data.profilePhotoUrl || "",
          providerBio: configData.data.providerBio || "",
        },
        { keepDirty: false }
      );

      setCharCount(configData.data.providerBio?.length || 0);
    }
  }, [configData, reset]);

  // Blob URL cleanup is handled by the useFileUpload hook inside AvatarUpload,
  // so no cleanup is needed here.

  const performGeneralSave = (data) => {
    return new Promise((resolve) => {
      updateGeneral(data, {
        onSuccess: () => {
          setProfilePhotoFile(null);
          resolve({ ok: true });
        },
        onError: (err) => {
          resolve({ ok: false, err });
        },
      });
    });
  };

  const flashSuccess = (message) => {
    setSuccessMessage(message);
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  // --- Removal handler with auto-save ---

  const handleConfirmedPhotoRemove = async () => {
    setValue("profilePhotoUrl", null, { shouldDirty: true });
    setProfilePhotoFile(null);
    setErrors({});
    setSuccess(false);
    setShowPhotoRemoveConfirm(false);

    const formData = new FormData();
    formData.append("profilePhotoUrl", "null");
    const result = await performGeneralSave(formData);

    if (!result.ok) {
      // Restore the previous value — the removal did not take effect.
      setValue("profilePhotoUrl", originalProfilePhotoUrlRef.current, {
        shouldDirty: false,
      });
      setErrors({
        title: "Error",
        message: buildApiErrorMessage(
          result.err,
          "Failed to remove profile photo"
        ),
      });
      return;
    }

    flashSuccess("Profile photo removed successfully");
  };

  // --- Main submit ---

  const onSubmit = async (data) => {
    setErrors({});
    setSuccess(false);

    // Validate file
    if (profilePhotoFile) {
      try {
        validateImageFile(profilePhotoFile);
      } catch (error) {
        setErrors({
          title: "File Validation Error",
          message: `Profile photo: ${error.message}`,
        });
        return;
      }
    }

    const generalFormData = new FormData();
    generalFormData.append("providerName", data.providerName ?? "");
    generalFormData.append("providerBio", data.providerBio ?? "");

    if (profilePhotoFile) {
      generalFormData.append("profilePhoto", profilePhotoFile);
    } else if (originalProfilePhotoUrlRef.current && !data.profilePhotoUrl) {
      // Explicit removal (user cleared it without confirming via modal)
      generalFormData.append("profilePhotoUrl", "null");
    }

    const result = await performGeneralSave(generalFormData);
    if (!result.ok) {
      setErrors({
        title: "Error",
        message: buildApiErrorMessage(
          result.err,
          "Failed to update provider information"
        ),
      });
      return;
    }

    const uploadLabel = profilePhotoFile ? " and profile photo uploaded" : "";
    flashSuccess(`Personal identity updated successfully${uploadLabel}`);
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <BookingPageFormSkeleton showFileUpload={true} inputCount={2} />
      </TabFormWrapper>
    );
  }

  return (
    <>
      {/* Profile photo removal confirmation */}
      <AlertModal
        isOpen={showPhotoRemoveConfirm}
        modalTitle="Remove Profile Photo"
        modalMessage={
          <>
            Are you sure you want to remove your profile photo? This action
            cannot be undone.
          </>
        }
        primaryCaption="Remove Photo"
        primaryButtonColour="red"
        secondaryCaption="Cancel"
        primaryClickHandler={handleConfirmedPhotoRemove}
        dismissModalHandler={() => setShowPhotoRemoveConfirm(false)}
        showIcon={true}
      />

      <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
        <div className="px-4 py-6 sm:p-8">
          <div className="space-y-6">
            {/* Provider name */}
            <Input
              label="Provider Name"
              name="providerName"
              placeholder="Your name"
              errormessage={errors?.providerName?.message || formErrors?.providerName?.message}
              {...register("providerName", {
                maxLength: {
                  value: 100,
                  message: "Provider name cannot exceed 100 characters",
                },
                onBlur: createTrimHandler(setValue, "providerName"),
              })}
            />

            {/* Profile photo */}
            <AvatarUpload
              label="Profile Photo"
              name="profilePhoto"
              accept="image/*"
              defaulturl={configData?.data?.profilePhotoUrl || null}
              filelist={profilePhotoFile ? [profilePhotoFile] : []}
              onRemoveHandler={() => {
                setProfilePhotoFile(null);
                setValue("profilePhotoUrl", null, { shouldDirty: true });
              }}
              onRemoveConfirmHandler={() => setShowPhotoRemoveConfirm(true)}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setProfilePhotoFile(e.target.files[0]);
                  setValue("profilePhotoUrl", e.target.files[0].name, {
                    shouldDirty: true,
                  });
                }
              }}
            />

            {/* Bio */}
            <TextArea
              label="About Me"
              name="providerBio"
              rows={4}
              placeholder="Tell clients about yourself and your services..."
              charCount={charCount}
              maxLength={maxCharacters}
              errormessage={errors?.providerBio?.message || formErrors?.providerBio?.message}
              {...register("providerBio", {
                maxLength: {
                  value: maxCharacters,
                  message: `About me cannot exceed ${maxCharacters} characters`,
                },
                onBlur: (e) => {
                  const trimmed = e.target.value.trim();
                  setValue("providerBio", trimmed, {
                    shouldValidate: true,
                  });
                  // Keep the counter in sync with the trimmed value that will
                  // actually be submitted.
                  setCharCount(trimmed.length);
                },
                onChange: (e) => {
                  setCharCount(e.target.value.length);
                  setValue("providerBio", e.target.value, {
                    shouldValidate: true,
                  });
                },
              })}
            />
          </div>
        </div>

        <FormFooter
          primaryCaption="Save"
          isLoading={isUpdating}
          disabled={!isDirty && !profilePhotoFile}
          requiresVerification={false}
        />

        {errors.title && (
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <AlertRed title={errors.title} message={errors.message} />
          </div>
        )}

        {success && (
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <AlertGreen title="Success" message={successMessage} />
          </div>
        )}
      </TabFormWrapper>
    </>
  );
};

export default PersonalIdentityForm;
